import { useState, useRef, useEffect } from 'react'
import { useTheme } from '@mui/material/styles'
import { sleep } from '../utilities/general'
import Button, { ButtonProps } from '@mui/material/Button'
import Box, { BoxProps } from '@mui/material/Box'

import { LuCheck, LuCopy } from 'react-icons/lu'
import { PrismLight as SyntaxHighlighter } from 'react-syntax-highlighter'
import darkTheme from 'react-syntax-highlighter/dist/esm/styles/prism/material-dark'
import lightTheme from 'react-syntax-highlighter/dist/esm/styles/prism/material-light'
import bash from 'react-syntax-highlighter/dist/esm/languages/prism/bash'
import yaml from 'react-syntax-highlighter/dist/esm/languages/prism/yaml'

SyntaxHighlighter.registerLanguage('bash', bash)
SyntaxHighlighter.registerLanguage('yaml', yaml)

export interface CodeBlockProps extends BoxProps {
  code: string
  color?: ButtonProps['color']
  language?: 'yaml' | 'bash'
  disabled?: boolean
  hidden?: boolean
  firstTabActive?: boolean
  onCopy?: () => void
}

const CodeBlock: React.FC<CodeBlockProps> = ({
  code,
  color,
  disabled,
  language = 'bash',
  hidden,
  firstTabActive,
  onCopy,
  ...props
}) => {
  const theme = useTheme()
  const [copying, setCopying] = useState<boolean>(false)
  const [lastHeight, setLastHeight] = useState<number>(0)
  const [height, setHeight] = useState<number>(0)
  const codeRef = useRef<HTMLPreElement>(null)

  useEffect(() => {
    if (codeRef.current) {
      const newHeight = codeRef.current.scrollHeight
      setHeight(lastHeight)
      setLastHeight(newHeight)
      setTimeout(() => setHeight(newHeight), 1)
      setTimeout(() => setHeight(0), 600)
    }
  }, [code])

  const handleCopy = async () => {
    setCopying(true)
    navigator.clipboard.writeText(code)
    await sleep(1000)
    setCopying(false)
    onCopy?.()
  }

  return (
    <Box
      {...props}
      ref={codeRef}
      sx={{
        ...props.sx,
        position: 'relative',
        cursor: 'text',
        'code span': {
          ...theme.typography.code,
          backgroundColor: theme.palette.background.paper,
        },

        height: height ? `${height}px` : 'auto',
        transition: 'height 500ms ease-in-out',
        overflow: 'hidden',
      }}
    >
      <SyntaxHighlighter
        language={language}
        style={theme.palette.mode === 'dark' ? darkTheme : lightTheme}
        customStyle={{
          ...theme.typography.code,
          marginTop: 0,
          marginBottom: theme.spacing(1.5),
          paddingTop: theme.spacing(1.5),
          paddingBottom: theme.spacing(1.5),
          borderRadius: theme.shape.borderRadius,
          borderTopLeftRadius: firstTabActive ? 0 : theme.shape.borderRadius,
          background: theme.palette.background.paper,
          borderColor: theme.palette.contrast.minimal,
          borderStyle: 'solid',
          borderWidth: 1,
        }}
        className={hidden ? 'ph-mask' : disabled ? 'disabled' : ''}
      >
        {code}
      </SyntaxHighlighter>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Button
          size="small"
          disabled={disabled}
          variant="contained"
          color={color}
          onClick={handleCopy}
          endIcon={copying ? <LuCheck /> : <LuCopy />}
        >
          {copying ? 'Copied!' : 'Copy'}
        </Button>
      </Box>
    </Box>
  )
}

export default CodeBlock
